// Length / angle formatting and the mm | inch display unit. Pure apart from
// the saved preference, so it can be unit tested without a DOM.
import { loadLengthUnit, saveLengthUnit, type LengthUnit } from './preferences';

export type { LengthUnit };

const MM_PER_INCH = 25.4;
let lengthUnit: LengthUnit = loadLengthUnit();
const unitListeners: Array<() => void> = [];

export function getLengthUnit(): LengthUnit {
  return lengthUnit;
}

/** Switch the display unit; values are re-rendered by the registered listeners. */
export function setLengthUnit(unit: LengthUnit): void {
  if (unit === lengthUnit) return;
  lengthUnit = unit;
  saveLengthUnit(unit);
  for (const cb of unitListeners) cb();
}

export function onLengthUnitChange(cb: () => void): void {
  unitListeners.push(cb);
}

/** A length (given in mm) in the chosen display unit, e.g. "12.50 mm" or "0.492 in". */
export function formatDistance(mm: number): string {
  if (lengthUnit === 'in') return (mm / MM_PER_INCH).toFixed(3) + ' in';
  if (Math.abs(mm) >= 1000) return (mm / 1000).toFixed(3) + ' m';
  return mm.toFixed(2) + ' mm';
}

/** Like formatDistance with fewer decimals (section offset readout). */
export function formatOffset(mm: number): string {
  if (lengthUnit === 'in') return (mm / MM_PER_INCH).toFixed(3) + ' in';
  return mm.toFixed(1) + ' mm';
}

/** "X × Y × Z unit" for an overall size given in mm. */
export function formatSize(x: number, y: number, z: number): string {
  if (lengthUnit === 'in') {
    const f = (v: number): string => (v / MM_PER_INCH).toFixed(3);
    return `${f(x)} × ${f(y)} × ${f(z)} in`;
  }
  // Buildings arrive here too now that glTF is read in metres; six digits of
  // millimetres per axis is unreadable.
  if (Math.max(Math.abs(x), Math.abs(y), Math.abs(z)) >= 1000) {
    const m = (v: number): string => (v / 1000).toFixed(2);
    return `${m(x)} × ${m(y)} × ${m(z)} m`;
  }
  const f = (v: number): string => (v >= 100 ? v.toFixed(1) : v.toFixed(2));
  return `${f(x)} × ${f(y)} × ${f(z)} mm`;
}

export function formatAngleDeg(deg: number): string {
  return deg.toFixed(2) + ' deg';
}
